import { useState } from "react";
import { toast } from "sonner";
import { X, Mail, Loader2, Send, ShieldCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useT } from "@/i18n/LanguageProvider";

/**
 * WealthPlanDialog – collects name / email / phone from the visitor,
 * calls POST /api/wealth-plan which generates a personalised PDF and
 * emails it to the lead + admin. Shown modally when triggered from the
 * scenario explorer on /vermoegensaufbau.
 *
 * Props:
 *   open          boolean
 *   onOpenChange  (open: boolean) => void
 *   scenario      { apartmentId (backend id), apartmentName, equity, rate, years, rentPerM2 }
 */
export const WealthPlanDialog = ({ open, onOpenChange, scenario }) => {
    const t = useT();
    const [form, setForm] = useState({ name: "", email: "", phone: "", privacy_consent: false });
    const [submitting, setSubmitting] = useState(false);

    if (!open) return null;

    const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));
    const close = () => {
        if (!submitting) onOpenChange(false);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.privacy_consent) {
            toast.error(t({ de: "Bitte stimmen Sie den Datenschutzhinweisen zu.", en: "Please accept the privacy notice." }));
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/wealth-plan", {
                name: form.name,
                email: form.email,
                phone: form.phone || null,
                apartment_id: scenario.apartmentId,
                equity: scenario.equity,
                rate: scenario.rate,
                years: scenario.years,
                rent_per_m2: scenario.rentPerM2,
                tax_rate_pct: scenario.taxRatePct,
                privacy_consent: true,
            });
            toast.success(
                t({ de: "Plan wurde versendet", en: "Plan sent" }),
                {
                    description: t({
                        de: `Ihr persönlicher Vermögensaufbau-Plan für die ${scenario.apartmentName} liegt in Kürze in Ihrem Postfach.`,
                        en: `Your personalised wealth plan for ${scenario.apartmentName} will arrive in your inbox shortly.`,
                    }),
                    duration: 6000,
                },
            );
            setForm({ name: "", email: "", phone: "", privacy_consent: false });
            onOpenChange(false);
            // optional: tiny analytics hook for future admin chart
            if (data?.summary) {
                // eslint-disable-next-line no-console
                console.log("[wealth-plan] sent:", data.summary);
            }
        } catch (err) {
            toast.error(
                t({ de: "Anfrage konnte nicht gesendet werden", en: "Request could not be sent" }),
                { description: formatApiErrorDetail(err.response?.data?.detail) || err.message },
            );
        } finally {
            setSubmitting(false);
        }
    };

    const fmtEUR = (n) => Math.round(n).toLocaleString("de-DE") + " €";

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-sage-700/70 backdrop-blur-sm"
            onClick={close}
            data-testid="wealth-plan-dialog"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-xl bg-white text-sage-700 rounded-3xl shadow-2xl max-h-[90vh] overflow-y-auto"
            >
                <button
                    type="button"
                    onClick={close}
                    aria-label="close"
                    className="absolute top-4 right-4 p-2 rounded-full text-sage-500/60 hover:text-sage-700 hover:bg-mint-50"
                    data-testid="wealth-plan-close"
                >
                    <X size={18} />
                </button>

                <div className="p-8 lg:p-10">
                    <div className="flex items-center gap-2 mb-4">
                        <Mail size={18} className="text-terracotta-500" />
                        <span className="font-body text-xs uppercase tracking-[0.22em] font-semibold text-terracotta-500">
                            {t({ de: "Mein Vermögensaufbau-Plan", en: "My wealth-building plan" })}
                        </span>
                    </div>
                    <h2 className="font-display font-black tracking-tight text-sage-500 text-3xl sm:text-4xl leading-tight text-balance">
                        {t({
                            de: "Ihre Zahlen — persönlich per E-Mail.",
                            en: "Your numbers — personalised by email.",
                        })}
                    </h2>
                    <p className="font-body text-base text-sage-500/80 leading-relaxed mt-4">
                        {t({
                            de: (
                                <>
                                    Wir schicken Ihnen Ihren <strong>individuellen Vermögensaufbau-Plan</strong> als
                                    PDF zu: Ihre Zahlen für die <strong>{scenario.apartmentName}</strong> mit{" "}
                                    <strong>{fmtEUR(scenario.equity)}</strong> Eigenkapital – Rate, Cashflow, Break-Even,
                                    30-Jahres-Prognose. Diskret, ohne Spam. Versand erfolgt innerhalb weniger Minuten.
                                </>
                            ),
                            en: (
                                <>
                                    We'll send you your <strong>personal wealth-building plan</strong> as a PDF:
                                    your numbers for the <strong>{scenario.apartmentName}</strong> with{" "}
                                    <strong>{fmtEUR(scenario.equity)}</strong> equity – rate, cashflow, break-even,
                                    30-year forecast. Discreet, no spam. Sent within minutes.
                                </>
                            ),
                        })}
                    </p>

                    <form onSubmit={submit} className="mt-6 space-y-5" data-testid="wealth-plan-form">
                        <div className="grid sm:grid-cols-2 gap-5">
                            <div>
                                <label className="label-line">{t({ de: "Name *", en: "Name *" })}</label>
                                <input
                                    type="text"
                                    required
                                    value={form.name}
                                    onChange={(e) => update("name", e.target.value)}
                                    className="input-line"
                                    placeholder={t({ de: "Vor- und Nachname", en: "First and last name" })}
                                    data-testid="wealth-plan-name"
                                />
                            </div>
                            <div>
                                <label className="label-line">{t({ de: "E-Mail *", en: "Email *" })}</label>
                                <input
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={(e) => update("email", e.target.value)}
                                    className="input-line"
                                    placeholder="name@firma.de"
                                    data-testid="wealth-plan-email"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="label-line">{t({ de: "Telefon (optional)", en: "Phone (optional)" })}</label>
                            <input
                                type="tel"
                                value={form.phone}
                                onChange={(e) => update("phone", e.target.value)}
                                className="input-line"
                                placeholder="+49 …"
                                data-testid="wealth-plan-phone"
                            />
                        </div>

                        <label className="flex items-start gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={form.privacy_consent}
                                onChange={(e) => update("privacy_consent", e.target.checked)}
                                className="mt-1 h-4 w-4 rounded border-black/20 text-terracotta-500 focus:ring-terracotta-500"
                                required
                                data-testid="wealth-plan-consent"
                            />
                            <span className="font-body text-xs text-sage-500/80 leading-relaxed">
                                {t({
                                    de: (
                                        <>
                                            Ich willige ein, dass meine Angaben zur Erstellung und Zusendung des Plans
                                            gespeichert und vom Bauträger genutzt werden. Weitere Informationen in der{" "}
                                            <a href="/datenschutz" className="underline hover:text-terracotta-600">Datenschutzerklärung</a>.
                                        </>
                                    ),
                                    en: (
                                        <>
                                            I consent that my data is stored and used by the developer to create and
                                            send the plan. More info in our{" "}
                                            <a href="/datenschutz" className="underline hover:text-terracotta-600">privacy policy</a>.
                                        </>
                                    ),
                                })}
                            </span>
                        </label>

                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-sage-500 text-white font-body font-medium hover:bg-sage-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                            data-testid="wealth-plan-submit"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 size={16} className="animate-spin" />
                                    {t({ de: "Wird erstellt und versendet…", en: "Creating and sending…" })}
                                </>
                            ) : (
                                <>
                                    <Send size={16} />
                                    {t({ de: "Plan per E-Mail erhalten", en: "Receive plan by email" })}
                                </>
                            )}
                        </button>

                        <div className="flex items-start gap-2 pt-2 border-t border-black/5">
                            <ShieldCheck size={14} className="text-sage-500/60 shrink-0 mt-0.5" />
                            <p className="font-body text-[11px] text-sage-500/60 leading-relaxed">
                                {t({
                                    de: "Ihre Daten werden vertraulich behandelt. Wir nutzen sie ausschließlich, um Ihnen den Plan zu schicken und Ihr Interesse zu bearbeiten – keine Werbung, kein Newsletter.",
                                    en: "Your data is handled in confidence. We only use it to send the plan and process your interest – no marketing, no newsletter.",
                                })}
                            </p>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};
